/**
 * events/FusionEventBus.ts — publish/subscribe so engines never poll.
 *
 * When the Fusion Engine refreshes a domain it publishes an event (e.g.
 * WeatherUpdated). Other engines subscribe and react instead of re-fetching.
 * Events fire both through an in-process bus (fast, typed) and as window
 * CustomEvents (cross-module, testable in a DOM env).
 */

import type { FusionDomain } from '../models';

export type FusionEventName =
  | 'WeatherUpdated'
  | 'LocationUpdated'
  | 'HousingUpdated'
  | 'CurrencyUpdated'
  | 'SafetyUpdated'
  | 'EventsUpdated'
  | 'CommunityUpdated'
  | 'PlacesUpdated'
  | 'RoamerScoreUpdated'
  | 'ProviderHealthChanged';

export interface FusionEvent {
  name: FusionEventName;
  domain: FusionDomain | null;
  /** Provider that produced the data, when applicable. */
  providerId: string | null;
  at: number;
  /** How many records the refresh yielded (0 for a graceful empty state). */
  recordCount: number;
}

/** Map a domain to the coarse event name engines subscribe to. */
export function domainToEvent(domain: FusionDomain): FusionEventName {
  switch (domain) {
    case 'weather': return 'WeatherUpdated';
    case 'currency':
    case 'currencyExchange': return 'CurrencyUpdated';
    case 'housing':
    case 'hotel':
    case 'hostel':
    case 'monthlyRental': return 'HousingUpdated';
    case 'safety':
    case 'travelAdvisory': return 'SafetyUpdated';
    case 'events': return 'EventsUpdated';
    case 'communityReviews': return 'CommunityUpdated';
    default: return 'PlacesUpdated';
  }
}

type Listener = (event: FusionEvent) => void;

const WINDOW_EVENT_PREFIX = 'nomad:fusion:';

export class FusionEventBus {
  private listeners = new Map<FusionEventName, Set<Listener>>();
  private anyListeners = new Set<Listener>();

  /** Subscribe to one event name. Returns an unsubscribe function. */
  on(name: FusionEventName, listener: Listener): () => void {
    let set = this.listeners.get(name);
    if (!set) {
      set = new Set();
      this.listeners.set(name, set);
    }
    set.add(listener);
    return () => set!.delete(listener);
  }

  /** Subscribe to every event. Returns an unsubscribe function. */
  onAny(listener: Listener): () => void {
    this.anyListeners.add(listener);
    return () => this.anyListeners.delete(listener);
  }

  emit(event: FusionEvent): void {
    this.listeners.get(event.name)?.forEach((l) => l(event));
    this.anyListeners.forEach((l) => l(event));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(`${WINDOW_EVENT_PREFIX}${event.name}`, { detail: event }));
    }
  }
}

/** App-wide bus. */
export const fusionEventBus = new FusionEventBus();
